import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowPathIcon, SparklesIcon } from '@heroicons/react/24/outline';
import MarkdownContent from '../MarkdownContent';
import SubscriptionOrderDialog from './SubscriptionOrderDialog';
import { authService } from '../../services/auth';
import { i18nService } from '../../services/i18n';
import SubscriptionExtras from './SubscriptionExtras';
import { SubscriptionOrderRecoveryError, SubscriptionRequestError, type SubscriptionOrder, type SubscriptionPlan } from '../../services/subscription';

/** 套餐购买入口；父组件按登录周期挂载，订单归属由平台再次校验。 */
export default function SubscriptionPlans({ onPaid }: { onPaid: () => void }) {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [order, setOrder] = useState<SubscriptionOrder | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState<SubscriptionPlan | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const alive = useRef(false);
  const submitting = useRef(false);
  const paid = useRef('');
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;
  const t = (key: string) => i18nService.t(`subscription${key}`);
  const checkoutBlocked = Boolean(order && !['success', 'expired', 'failed', 'cancelled', 'refunded'].includes(order.status));
  const auth = authService.getAuthState();
  const key = `bomate-payment:${authService.getBaseUrl()}:${auth?.user.id ?? ''}`;

  /** 仅保存订单号用于刷新恢复；金额、二维码与状态每次从平台重新读取。 */
  const remember = useCallback((value: SubscriptionOrder | null) => {
    try { if (value) sessionStorage.setItem(key, JSON.stringify({ trade_no: value.trade_no, payment_method: value.payment_method || 'chinaums' })); else sessionStorage.removeItem(key); } catch { /* 隐私模式不妨碍付款。 */ }
  }, [key]);

  useEffect(() => {
    alive.current = true;
    void authService.subscriptions.plans().then(value => { if (alive.current) { setPlans(value); setLoaded(true); } })
      .catch(() => { if (alive.current) { setError(true); setLoaded(true); } });
    let previous: string | null = null;
    try { previous = sessionStorage.getItem(key); } catch { /* 存储不可用时仍可创建新订单。 */ }
    let restored = { trade_no: previous || '', payment_method: 'chinaums' };
    if (previous?.startsWith('{')) {
      try { const parsed = JSON.parse(previous); restored = { trade_no: String(parsed.trade_no || ''), payment_method: String(parsed.payment_method || '') }; }
      catch { restored.trade_no = ''; }
    }
    if (restored.trade_no) {
      setOrder({ ...restored, status: 'unknown', amount: null, amount_minor: null, expires_at: 0, qr_code: '' });
      setOpen(true);
      void (restored.payment_method === 'chinaums'
      ? authService.subscriptions.order(restored.trade_no) : authService.subscriptions.personalOrder(restored.trade_no)).then(value => {
      if (alive.current) { setOrder(value); setOpen(value.status === 'pending'); }
    }).catch(() => { if (alive.current) setError(true); });
    }
    return () => { alive.current = false; };
  }, [key]);

  /** 显式购买按钮触发唯一 POST，不在挂载或重试轮询中创建支付订单。 */
  const purchase = async (plan: SubscriptionPlan) => {
    if (submitting.current || busy || checkoutBlocked) return;
    submitting.current = true; setBusy(true); setError(false);
    setCheckoutPlan(plan); setCheckoutError(null); setOrder(null); setOpen(true);
    try {
      const value = await authService.subscriptions.create(plan.id);
      if (!alive.current) return;
      setOrder(value); setOpen(true); remember(value);
    } catch (error) {
      if (alive.current) {
        setCheckoutError(error instanceof SubscriptionRequestError ? error.message : t('Unavailable'));
        if (error instanceof SubscriptionOrderRecoveryError) { setOrder(error.recovery); remember(error.recovery); }
      }
    }
    finally { submitting.current = false; if (alive.current) setBusy(false); }
  };

  useEffect(() => {
    if (!order) return;
    if (order.status === 'success') {
      remember(null);
      if (paid.current !== order.trade_no) { paid.current = order.trade_no; onPaidRef.current(); }
      return;
    }
    if (!['pending', 'unknown'].includes(order.status)) {
      if (['expired', 'failed', 'cancelled', 'refunded'].includes(order.status)) remember(null);
      return;
    }
    let disposed = false;
    let inFlight = false;
    /** 串行轮询只查当前订单；网络失败保留订单并提示，恢复焦点立即再查。 */
    const refresh = async () => {
      if (inFlight || document.visibilityState !== 'visible') return;
      inFlight = true;
      try {
        const next = await (order.payment_method && order.payment_method !== 'chinaums'
          ? authService.subscriptions.personalOrder(order.trade_no) : authService.subscriptions.order(order.trade_no, true));
        if (!disposed) { setError(false); setOrder(old => old?.trade_no === next.trade_no ? { ...next, qr_code: next.qr_code || old.qr_code, pay_url: next.pay_url || old.pay_url } : old); }
      } catch { if (!disposed) setError(true); }
      finally { inFlight = false; }
    };
    const timer = window.setInterval(refresh, 5000);
    window.addEventListener('focus', refresh);
    return () => { disposed = true; window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [order?.trade_no, order?.status, remember]);

  return <section className="rounded-2xl border border-claude-border dark:border-claude-darkBorder p-4 space-y-3" data-testid="subscription-plans">
    <h3 className="font-medium">{t('Title')}</h3>
    {error && <p role="alert" className="text-sm text-amber-600">{t('Unavailable')}</p>}
    {!loaded && <p className="text-sm">{t('Loading')}</p>}
    {loaded && !error && plans.length === 0 && <p className="text-sm">{t('Empty')}</p>}
    <div className="grid items-stretch gap-3 sm:grid-cols-2">{plans.map(plan => <article key={plan.id} className="flex min-w-0 flex-col rounded-2xl border border-claude-border dark:border-claude-darkBorder bg-claude-surface dark:bg-claude-darkSurface p-4 shadow-sm">
      <h4 className="break-words text-base font-semibold">{plan.title}</h4>
      <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
        <span>{t('Base')}: {plan.credit_base?.toLocaleString(i18nService.getLanguage()) ?? '—'}</span>
        <span className="inline-flex items-center gap-1"><SparklesIcon aria-hidden="true" className="h-3.5 w-3.5 text-amber-500" />{t('Bonus')}: {plan.credit_bonus?.toLocaleString(i18nService.getLanguage()) ?? '—'}</span>
      </p>
      <p className="mt-3 break-all text-2xl font-semibold tabular-nums">{plan.currency === 'CNY' ? '¥' : `${plan.currency} `}{plan.price_amount.toFixed(2)}</p>
      <p className="mt-1 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">{t('Duration')}: {['year', 'month', 'day', 'hour'].includes(plan.duration_unit) && Number.isSafeInteger(plan.duration_value) && plan.duration_value > 0
        ? `${plan.duration_value} ${t(`Duration_${plan.duration_unit}`)}` : '—'}</p>
      <button type="button" disabled={busy || checkoutBlocked || plan.currency !== 'CNY'} onClick={() => void purchase(plan)} className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-claude-accent px-3 py-2.5 text-sm font-medium text-white transition-colors hover:brightness-95 disabled:opacity-50">
        {busy && <ArrowPathIcon aria-hidden="true" className="h-4 w-4 motion-safe:animate-spin" />}{t('Buy')}
      </button>
      {plan.currency !== 'CNY' && <p className="mt-2 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">{t('CnyOnly')}</p>}
      {plan.subtitle && <MarkdownContent content={plan.subtitle} className="mt-4 flex-1 break-words border-t border-claude-border dark:border-claude-darkBorder pt-3 [&]:text-xs [&]:leading-6 [&_p]:my-1 [&_ul]:my-1" />}
    </article>)}</div>
    {order && !open && <button type="button" onClick={() => setOpen(true)} className="text-sm text-claude-accent">{t('ViewOrder')}</button>}
    {open && <SubscriptionOrderDialog order={order} error={error} loading={busy && !order} errorMessage={checkoutError}
      onRetry={!order && checkoutPlan && !busy ? () => void purchase(checkoutPlan) : undefined} onClose={() => setOpen(false)} />}
    <SubscriptionExtras onBusyChange={setBusy} disabled={busy || checkoutBlocked} revision={`${order?.trade_no || ''}:${order?.status || ''}`}
      onOrder={value => { setCheckoutError(null); setOrder(value); setOpen(true); remember(value); }} />
  </section>;
}
